import { useState } from 'react';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useSetRecoilState } from 'recoil';

import OptionButton from '@/components/common/OptionButton/OptionButton';
import CheckTick from '@/components/common/OptionButton/icons/CheckIcon/CheckTick';
import useGetContractDetailsFromCache from '@/hooks/contract/useGetContractDetailsFromCache.hook';
import useGetContractLinesFromCache from '@/hooks/contract/useGetContractLinesFromCache.hook';
import { rightsidePanel_isOpen } from '../../../RightPanel.state';
import ApproveContractLinesModal from './components/ApproveContractLinesModal/ApproveContractLinesModal';
import EndDatePassedModal from './components/EndDatePassedModal/EndDatePassedModal';
import useApproveContractLines, {
  ApproveContractLinesInput,
} from './hooks/useApproveContractLines';
import RollOverDateModal from './components/RolloverDateModal/RollOverDateModal';
import { ContractStatuses } from '@/config/statuses.config';
import {
  clearLinesSelection,
  useSelectedLines
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import { useSelectedVariableLines, clearVariableLinesSelection } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';
import { ContractStatusFormatted } from '@/pages/ContractDetails/ContractLines/grid/variableContractLinescolumns';
import { ContractStatusEnum } from '@/graphql/contracts/_generated_/contracts';

export default function ActiveStatusContainer() {
  const { contractId }: { contractId: UUID } = useParams();
  const contract = useGetContractDetailsFromCache(contractId);
  const { cachedBCContractLines } = useGetContractLinesFromCache(contractId);
  const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
  const [isRollOverModalVisible, setIsRollOverModalVisible] = useState(false);
  const [isEndDatePassedModal, setIsEndDatePassedModal] = useState(false);
  const { approveContractLines } = useApproveContractLines();
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);
  const selectedLineRows = useSelectedLines();
  const selectedVariableLineRows = useSelectedVariableLines();
  const selectedLines = selectedLineRows.length ? selectedLineRows : selectedVariableLineRows;
  const variableLinesSelectedStatus = selectedVariableLineRows.length ? selectedVariableLineRows
    .find(cl => cl.id === selectedVariableLineRows?.[0]?.id)?.payload?.contractLine?.status as ContractStatusEnum
    : null;

  const contractLineStatus =
    cachedBCContractLines?.paginatedFilteredContractLinesByBillingCycles?.items?.
      find(cl => cl?.id === selectedLineRows?.[0]?.id)?.status;

  if (!cachedBCContractLines || !contract) {
    return null;
  }

  if (contract?.status.status === 'Inactive') {
    return null;
  }
  const contractEndDate = dayjs(contract.contractEndDate);
  const todayDate = dayjs();
  const contractEndDateHasPassed = contractEndDate.isBefore(todayDate, 'date');
  const contractLineIsDNR = contractLineStatus as any === ContractStatuses.DNR;
  const contractStatusIsDNROrActive = contract?.status.status === ContractStatuses.DNR
    || contract?.status.status === ContractStatuses.Active;
  const contractStatusAndContractLineAreValid = contractLineIsDNR && contractStatusIsDNROrActive;
  const contractRenewableTermIsNotZero = contract.renewableTerm > 0;
  const selectedLineStatus = contractLineStatus ?? ContractStatusFormatted[variableLinesSelectedStatus!];
  const onSave = (comment: ApproveContractLinesInput['comment']) => {
    const input = {
      comment,
      contractLines: selectedLines.map(({ id }) => ({
        contractLineID: id
      }))
    };

    approveContractLines(input, () => {
      setIsOpenRightPanel(false);
      return selectedLineRows.length ? clearLinesSelection() : clearVariableLinesSelection();
    });
  };

  const onClickOption = () => {
    if (contractEndDateHasPassed && contractStatusAndContractLineAreValid && contractRenewableTermIsNotZero) {
      setIsRollOverModalVisible(true);
    } else if (contractEndDateHasPassed) {
      setIsEndDatePassedModal(true);
    } else {
      setIsApproveModalVisible(true);
    }
  };
  return (
    <>
      {isApproveModalVisible && (
        <ApproveContractLinesModal
          onSave={onSave}
          onClose={() => setIsApproveModalVisible(false)}
          status={selectedLineStatus}
        />
      )}

      {isEndDatePassedModal && (
        <EndDatePassedModal onClose={() => setIsEndDatePassedModal(false)} />
      )}
      {isRollOverModalVisible && (
        <RollOverDateModal onClose={() => setIsRollOverModalVisible(false)}
          onConfirm={() => {
            setIsApproveModalVisible(true);
            setIsRollOverModalVisible(false);
          }} />
      )}

      <OptionButton
        Icon={<CheckTick />}
        onClick={onClickOption}
      >
        Activate line(s)
      </OptionButton>
    </>
  );
}
